import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft,
  Clock,
  CloudRain,
  ExternalLink,
  Link2,
  Loader2,
  MapPin,
  Radar,
  Route,
  ShieldAlert,
} from 'lucide-react';
import { toast } from 'sonner';
import { CommanServices } from '@/services/commanServices';
import { warningToEvent } from '../components/externalEvents';
import {
  claimSourceLabel,
  claimStatusLabel,
  collectEvidenceActivity,
  dedupeClaims,
  humanizeClaim,
} from '../components/foresightEvidence';
import {
  primaryClassificationOf,
  writtenLocationOf,
} from '../components/foresightClassification';
import {
  operationalScopeOf,
  uniqueEventAreaLabels,
} from '../components/foresightScope';
import './warningDetailLight.css';

const fmt = (iso) => (iso ? new Date(iso).toLocaleString() : 'Time unavailable');

const displayValue = (value) => {
  if (value === undefined || value === null) return '';
  if (typeof value === 'string' || typeof value === 'number') return String(value);
  return value?.label || value?.display_name || value?.name || '';
};

const locationLines = (warning, event, scope) => {
  const location = warning?.location || {};
  const resolution = warning?.location_resolution || warning?.hazard_gate?.location_resolution || {};
  const gps = event?.gps || location?.gps || warning?.gps;
  const areas = uniqueEventAreaLabels([
    ...(Array.isArray(location.affected_areas) ? location.affected_areas : []),
    ...(Array.isArray(location.affected_zones) ? location.affected_zones : []),
    location.nearest_zone,
  ], scope?.country);
  const coordinates = gps?.latitude != null && gps?.longitude != null
    ? `${Number(gps.latitude).toFixed(4)}, ${Number(gps.longitude).toFixed(4)}`
    : '';
  return [
    areas.length > 0 ? { label: 'Mapped area', value: areas.join(' · ') } : null,
    scope?.code === 'external' && scope.distance !== null
      ? { label: 'Proximity', value: `${scope.distance.toLocaleString()} km from Operations Area` }
      : null,
    coordinates ? { label: 'Coordinates', value: coordinates } : null,
    resolution.method || resolution.label
      ? { label: 'Location method', value: displayValue(resolution.method || resolution.label).replaceAll('_', ' ') }
      : null,
  ].filter(Boolean);
};

// Dedicated detail page for one validated ForeSight detection. Risk scoring,
// reassessment and dispatch are deliberately absent: this page keeps the
// authoritative detection, classification, location and evidence only.
const WarningDetail = () => {
  const { warningId } = useParams();
  const navigate = useNavigate();
  const [warning, setWarning] = useState(null);
  const [loading, setLoading] = useState(true);
  const [building, setBuilding] = useState(false);
  const [error, setError] = useState('');
  const [claimsExpanded, setClaimsExpanded] = useState(false);
  const [activityExpanded, setActivityExpanded] = useState(false);

  const load = useCallback(async () => {
    try {
      const detailRes = await CommanServices.getWarningDetail(warningId);
      const detail = detailRes?.data || detailRes;
      if (detailRes?.status === false || !detail?.warning) {
        setError(detailRes?.message || 'Detection not found');
        return;
      }
      setWarning(detail.warning);
      setError('');
    } catch (requestError) {
      setError(requestError?.message || 'Could not load this detection');
    } finally {
      setLoading(false);
    }
  }, [warningId]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const heartbeat = setInterval(load, 60000);
    const onFocus = () => load();
    window.addEventListener('focus', onFocus);
    window.addEventListener('earlyWarningsChanged', onFocus);
    return () => {
      clearInterval(heartbeat);
      window.removeEventListener('focus', onFocus);
      window.removeEventListener('earlyWarningsChanged', onFocus);
    };
  }, [load]);

  const buildScenario = async () => {
    if (building) return;
    setBuilding(true);
    try {
      const response = await CommanServices.scenarioPrefillFromWarning({ warning_id: warningId });
      const prefill = (response?.data || response)?.prefill;
      if (response?.status === false || !prefill) {
        toast.error(response?.message || 'Could not prepare the detected hazard for Scenario Builder');
        return;
      }
      navigate('/scenario-library', { state: { agentPrefill: prefill } });
    } catch (requestError) {
      toast.error(requestError?.message || 'Could not prepare the scenario');
    } finally {
      setBuilding(false);
    }
  };

  if (loading) {
    return (
      <div className="h-screen flex flex-1 overflow-hidden">
        <div className="flex-1 overflow-y-auto fs-light">
          <div className="fs-container" style={{ textAlign: 'center', color: '#94a3b8', paddingTop: 120 }}>
            <Loader2 className="h-5 w-5 animate-spin" style={{ display: 'inline' }} /> Loading detection…
          </div>
        </div>
      </div>
    );
  }

  if (error || !warning) {
    return (
      <div className="h-screen flex flex-1 overflow-hidden">
        <div className="flex-1 overflow-y-auto fs-light">
          <div className="fs-container" style={{ textAlign: 'center', paddingTop: 120 }}>
            <p style={{ color: '#64748b', fontSize: 14, marginBottom: 12 }} data-testid="warning-detail-error">
              {error || 'Detection not found'}
            </p>
            <button className="fs-back" onClick={() => navigate('/foresight')}>Back to ForeSight</button>
          </div>
        </div>
      </div>
    );
  }

  const event = {
    ...warningToEvent(warning),
    distance_km: warning.distance_km ?? warning.location?.distance_km ?? null,
    nearest_zone: warning.nearest_zone || warning.location?.nearest_zone || null,
  };
  const scope = operationalScopeOf(warning);
  const activity = collectEvidenceActivity(warning);
  const hazards = warning.hazards || [];
  const isStormy = hazards.some((hazard) => ['flood', 'storm', 'sandstorm'].includes(hazard));
  const locations = locationLines(warning, event, scope);
  const classification = primaryClassificationOf(warning);
  const writtenLocation = writtenLocationOf(warning);
  const claims = dedupeClaims(warning.claims);
  const claimsAreUnresolved = claims.every((claim) =>
    ['Reported', 'Corroborated'].includes(claimStatusLabel(claim))
  );

  return (
    <div className="h-screen flex flex-1 overflow-hidden">
      <div className="flex-1 overflow-y-auto fs-light">
        <div className="fs-container" data-testid="warning-detail-page">
          <button className="fs-back" onClick={() => navigate('/foresight')}>
            <ArrowLeft size={15} strokeWidth={2} /> ForeSight · Detected hazards
          </button>

          <div className={`fs-header-card fs-header-card--scope-${scope.code}`}>
            <div className="fs-icon-square">
              {isStormy ? <CloudRain size={26} strokeWidth={2} /> : <Radar size={26} strokeWidth={2} />}
            </div>
            <div style={{ minWidth: 0, flex: 1 }}>
              <h1 className="fs-title">
                {warning.hazard_gate?.normalized_title || warning.normalized_title || warning.title}
              </h1>
              <div className="fs-meta">
                <span className="fs-live-dot" title="Live — refreshes when new evidence arrives" />
                <span className="fs-meta-source">
                  Detected by {warning.source_name || event.connection?.name || 'external source'}
                </span>
                <span><Clock size={13} strokeWidth={2} /> {fmt(warning.occurred_at || warning.created_at)}</span>
                {hazards.map((hazard) => (
                  <span key={hazard} className="fs-pill fs-pill-hazard">{hazard.toUpperCase()}</span>
                ))}
                <span className="fs-pill fs-pill-corr">
                  {activity.length} linked {activity.length === 1 ? 'record' : 'records'}
                </span>
                <span
                  className={`fs-scope-badge fs-scope-badge--${scope.code}`}
                  data-testid="warning-operational-scope"
                >
                  <b>{scope.label}</b>
                  <small>{scope.detail}</small>
                </span>
              </div>
            </div>
          </div>

          <div className="fs-grid fs-grid--detection">
            <div className="fs-col">
              {warning.description && (
                <div className="fs-card fs-card-pad" data-testid="warning-detection-summary">
                  <p className="fs-eyebrow">Detection summary</p>
                  <p className="fs-rail-text">{warning.description}</p>
                </div>
              )}

              <div className="fs-card fs-card-pad" data-testid="warning-classification-location">
                <p className="fs-eyebrow"><MapPin size={13} strokeWidth={2} /> Classification &amp; location</p>
                <div className="fs-classification-path" data-testid="warning-classification-path">
                  {[
                    ['Incident Type', classification.incidentType],
                    ['Event', classification.event],
                    ['Trigger', classification.trigger],
                  ].map(([label, value], index) => (
                    <React.Fragment key={label}>
                      {index > 0 && <span className="fs-classification-arrow" aria-hidden="true">→</span>}
                      <span className={`fs-classification-chip ${value ? '' : 'is-empty'}`}>
                        <small>{label}</small>
                        <b>{value || 'Not specified'}</b>
                      </span>
                    </React.Fragment>
                  ))}
                </div>
                <div className="fs-written-location" data-testid="warning-written-location">
                  <MapPin size={14} strokeWidth={2} />
                  <span>{writtenLocation || 'Location unresolved'}</span>
                </div>
                <div className="fs-detection-fields">
                  <div>
                    <span>Hazard types</span>
                    <b>{hazards.length > 0 ? hazards.join(' · ') : 'Not classified'}</b>
                  </div>
                  {locations.map((entry) => (
                    <div key={`${entry.label}-${entry.value}`}>
                      <span>{entry.label}</span>
                      <b>{entry.value}</b>
                    </div>
                  ))}
                  {locations.length === 0 && (
                    <div>
                      <span>Location</span>
                      <b>Unresolved — this detection remains unmapped</b>
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="fs-rail">
              {claims.length > 0 && (
                <div className="fs-card fs-card-pad" data-testid="warning-reported-details">
                  <p className="fs-eyebrow">
                    <ShieldAlert size={13} strokeWidth={2} />
                    {claimsAreUnresolved ? 'Not yet confirmed' : 'Reported details'}
                  </p>
                  <p className="fs-claim-caption">
                    {claimsAreUnresolved ? 'Reported details' : 'Source-attributed claim status'}
                  </p>
                  <div className="fs-reported-details">
                    {(claimsExpanded ? claims : claims.slice(0, 2)).map((claim, index) => {
                      const source = claimSourceLabel(claim);
                      const status = claimStatusLabel(claim);
                      return (
                        <div className="fs-reported-detail" key={`${humanizeClaim(claim)}-${index}`}>
                          <p>{humanizeClaim(claim)}</p>
                          <div>
                            {source && <span>Source: {source}</span>}
                            <span className={`fs-claim-status fs-claim-status--${status.toLowerCase()}`}>{status}</span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  {claims.length > 2 && (
                    <button
                      type="button"
                      className="fs-compact-expand"
                      onClick={() => setClaimsExpanded((current) => !current)}
                      aria-expanded={claimsExpanded}
                      data-testid="warning-expand-claims"
                    >
                      {claimsExpanded ? 'Show fewer' : `Show ${claims.length - 2} more`}
                    </button>
                  )}
                </div>
              )}

              <div className="fs-card fs-card-pad" data-testid="warning-evidence-card">
                <p className="fs-eyebrow"><Link2 size={13} strokeWidth={2} /> Evidence &amp; activity</p>
                {activity.length > 0 ? (
                  <>
                    <p className="fs-corr-count">{activity.length} linked {activity.length === 1 ? 'record' : 'records'}</p>
                    <div className="fs-activity-stream" data-testid="warning-evidence-activity">
                      {(activityExpanded ? activity : activity.slice(0, 2)).map((entry, index) => (
                        <div key={`${entry.key}-${index}`} className="fs-activity-entry">
                          <div className="fs-activity-meta">
                            <b>{entry.publisher || 'Source'}</b>
                            {entry.timestamp && <time dateTime={entry.timestamp}>{fmt(entry.timestamp)}</time>}
                            <span className={`fs-activity-status fs-activity-status--${entry.status.toLowerCase().replaceAll(' ', '-')}`}>
                              {entry.status}
                            </span>
                          </div>
                          {entry.url ? (
                            <a href={entry.url} target="_blank" rel="noreferrer">
                              <span dir="auto">{entry.title}</span> <ExternalLink size={11} />
                            </a>
                          ) : (
                            <span dir="auto">{entry.title}</span>
                          )}
                        </div>
                      ))}
                    </div>
                    {activity.length > 2 && (
                      <button
                        type="button"
                        className="fs-compact-expand"
                        onClick={() => setActivityExpanded((current) => !current)}
                        aria-expanded={activityExpanded}
                        data-testid="warning-expand-activity"
                      >
                        {activityExpanded ? 'Show fewer' : `Show ${activity.length - 2} more`}
                      </button>
                    )}
                  </>
                ) : (
                  <p className="fs-rail-text">No linked evidence found yet.</p>
                )}
              </div>

              <button
                type="button"
                className="fs-build-scenario"
                onClick={buildScenario}
                disabled={building}
                data-testid="warning-build-scenario"
              >
                {building ? <Loader2 size={16} className="animate-spin" /> : <Route size={16} strokeWidth={2} />}
                {building ? 'Preparing Scenario Builder…' : 'Build Scenario'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WarningDetail;
